import { Injectable } from '@angular/core';

interface ConsentData {
  acceptedKeys: string[];
  declinedAt: number | null;
  allAccepted: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class ConsentStorageService {
  private readonly STORAGE_KEY_PREFIX = 'pdn-consent';
  private readonly HOURS_BEFORE_RETRY = 24;

  /**
   * Получить принятые ключи соглашений для пользователя
   */
  getAcceptedKeys(userId: string): string[] {
    const data = this.getData(userId);
    return data.acceptedKeys;
  }

  /**
   * Добавить принятый ключ соглашения для пользователя
   */
  addAcceptedKey(userId: string, key: string): void {
    const data = this.getData(userId);
    
    if (!data.acceptedKeys.includes(key)) {
      data.acceptedKeys.push(key);
      this.saveData(userId, data);
    }
  }

  isAllAccepted(userId: string): boolean {
    const data = this.getData(userId);
    return data.allAccepted;
  }

  markAllAccepted(userId: string): void {
    const data = this.getData(userId);
    data.allAccepted = true;
    this.saveData(userId, data);
  }

  /**
   * Сохранить timestamp отказа от соглашения
   */
  saveDeclineTimestamp(userId: string): void {
    const data = this.getData(userId);
    data.declinedAt = Date.now();
    this.saveData(userId, data);
  }

  /**
   * Очистить timestamp отказа (при принятии соглашения)
   */
  clearDeclineTimestamp(userId: string): void {
    const data = this.getData(userId);
    if (data.declinedAt !== null) {
      data.declinedAt = null;
      this.saveData(userId, data);
    }
  }

  /**
   * Проверить, можно ли показывать соглашения (прошло ли 24 часа с отказа)
   */
  canShowConsent(userId: string): boolean {
    const data = this.getData(userId);
    
    if (data.declinedAt === null) {
      return true;
    }

    const now = Date.now();
    const hoursPassed = (now - data.declinedAt) / (1000 * 60 * 60);
    
    return hoursPassed >= this.HOURS_BEFORE_RETRY;
  }

  /**
   * Получить данные из localStorage для пользователя
   */
  private getData(userId: string): ConsentData {
    const key = this.getStorageKey(userId);
    const stored = localStorage.getItem(key);

    if (!stored) {
      return this.getDefaultData();
    }

    try {
      const parsed = JSON.parse(stored);
      return {
        acceptedKeys: Array.isArray(parsed.acceptedKeys) ? parsed.acceptedKeys : [],
        declinedAt: typeof parsed.declinedAt === 'number' ? parsed.declinedAt : null,
        allAccepted: parsed.allAccepted === true,
      };
    } catch {
      return this.getDefaultData();
    }
  }

  /**
   * Сохранить данные в localStorage для пользователя
   */
  private saveData(userId: string, data: ConsentData): void {
    const key = this.getStorageKey(userId);
    localStorage.setItem(key, JSON.stringify(data));
  }

  /**
   * Получить ключ для localStorage
   */
  private getStorageKey(userId: string): string {
    return `${this.STORAGE_KEY_PREFIX}-${userId}`;
  }

  /**
   * Получить данные по умолчанию
   */
  private getDefaultData(): ConsentData {
    return {
      acceptedKeys: [],
      declinedAt: null,
      allAccepted: false,
    };
  }
}
